import { parseJsonWithDetailedError, unknownKeyMessages } from '@/lib/json-editor-diagnostics'
import { DEFAULT_AGENT_TIMEZONE } from '@/lib/timezones'

type AgentJsonDraft = {
  name: string
  display_name: string
  description: string
  is_enabled: boolean
  timezone: string
  base_prompt: string
  custom_instructions: string
  tool_ids: string[]
  document_ids: number[]
  audience: 'internal' | 'public'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

const AGENT_ROOT_KEYS = [
  'name',
  'display_name',
  'description',
  'is_enabled',
  'timezone',
  'base_prompt',
  'custom_instructions',
  'tool_ids',
  'document_ids',
  'audience',
] as const

export function validateAgentJsonPayload(value: unknown): string[] {
  const errors: string[] = []
  if (!isRecord(value)) return ['El JSON debe ser un objeto.']
  unknownKeyMessages(value, AGENT_ROOT_KEYS, 'raíz').forEach((message) => errors.push(message))

  if (typeof value.name !== 'string') errors.push('`name` debe ser string.')
  if (typeof value.name === 'string' && !value.name.trim()) errors.push('`name` no puede estar vacío.')
  if (typeof value.display_name !== 'string') errors.push('`display_name` debe ser string.')
  if (typeof value.display_name === 'string' && !value.display_name.trim()) errors.push('`display_name` no puede estar vacío.')
  if (typeof value.description !== 'string') errors.push('`description` debe ser string.')
  if (typeof value.is_enabled !== 'boolean') errors.push('`is_enabled` debe ser boolean.')
  if (typeof value.timezone !== 'string') errors.push('`timezone` debe ser string.')
  if (typeof value.timezone === 'string' && !value.timezone.trim()) errors.push('`timezone` no puede estar vacío.')
  if (typeof value.base_prompt !== 'string') errors.push('`base_prompt` debe ser string.')
  if (typeof value.base_prompt === 'string' && !value.base_prompt.trim()) errors.push('`base_prompt` no puede estar vacío.')
  if (typeof value.custom_instructions !== 'string') errors.push('`custom_instructions` debe ser string.')
  if (!Array.isArray(value.tool_ids)) {
    errors.push('`tool_ids` debe ser array.')
  } else {
    const invalid = value.tool_ids.some((item) => typeof item !== 'string' && typeof item !== 'number')
    if (invalid) errors.push('`tool_ids` debe contener solo strings (o numbers convertibles).')
  }
  if (!Array.isArray(value.document_ids)) {
    errors.push('`document_ids` debe ser array.')
  } else {
    const invalid = value.document_ids.some((item) => !Number.isInteger(Number(item)) || Number(item) <= 0)
    if (invalid) errors.push('`document_ids` debe contener solo IDs numéricos positivos.')
  }
  if (value.audience !== undefined && value.audience !== 'internal' && value.audience !== 'public') {
    errors.push('`audience` debe ser "internal" | "public".')
  }

  return errors
}

export function validateAgentJsonText(source: string): string | null {
  if (!source.trim()) return 'El JSON no puede estar vacío.'
  const parsed = parseJsonWithDetailedError(source)
  if (parsed.error) return parsed.error
  const errors = validateAgentJsonPayload(parsed.value)
  return errors.length > 0 ? errors.join('\n') : null
}

export function normalizeAgentJsonPayload(value: unknown): AgentJsonDraft {
  const source = (isRecord(value) ? value : {}) as Record<string, unknown>
  return {
    name: typeof source.name === 'string' ? source.name : '',
    display_name: typeof source.display_name === 'string' ? source.display_name : '',
    description: typeof source.description === 'string' ? source.description : '',
    is_enabled: typeof source.is_enabled === 'boolean' ? source.is_enabled : true,
    timezone: typeof source.timezone === 'string' && source.timezone.trim() ? source.timezone : DEFAULT_AGENT_TIMEZONE,
    base_prompt: typeof source.base_prompt === 'string' ? source.base_prompt : '',
    custom_instructions: typeof source.custom_instructions === 'string' ? source.custom_instructions : '',
    tool_ids: Array.isArray(source.tool_ids)
      ? source.tool_ids.map((item) => String(item)).filter((item) => item.trim().length > 0)
      : [],
    document_ids: Array.isArray(source.document_ids)
      ? source.document_ids
          .map((item) => Number(item))
          .filter((item) => Number.isInteger(item) && item > 0)
      : [],
    audience: source.audience === 'public' ? 'public' : 'internal',
  }
}
